import { sum } from 'lib/array/arrayUtils'
import { buildItemPolicyLookup } from './buildItemPolicyEvaluationItem'
import { resolveCartLineTravellers, type CartLineContext } from './resolveCartLineTravellers'
import type { CartEvaluationItemInputWire } from 'businessTraveller/api/requestPostCartEvaluation'

// Flight: one line per flight item view; composite key mirrors postOrderUtils
export function buildFlightCartEvaluationItems(
  flightItemsViews: App.WithDataStatus<Array<App.Checkout.FlightItemView>>,
  context: CartLineContext,
): Array<CartEvaluationItemInputWire> {
  if (!flightItemsViews.hasRequiredData) {
    return []
  }

  const items: Array<CartEvaluationItemInputWire> = []

  for (const flightItemView of flightItemsViews.data) {
    const firstJourney = flightItemView.flights[0]
    if (!firstJourney || !flightItemView.item.hasFinalFare) {
      continue
    }

    const journeyFlights = firstJourney.journeyFlight.flights
    const departingAirportCode = journeyFlights[0]?.departingAirport
    const arrivingAirportCode = journeyFlights[journeyFlights.length - 1]?.arrivalAirport
    const cost =
      flightItemView.totals.price +
      (flightItemView.totals.otherFees
        ? sum(Object.values(flightItemView.totals.otherFees).map((fee) => fee ?? 0))
        : 0)

    if (!cost || !departingAirportCode || !arrivingAirportCode) {
      continue
    }

    // Every flight segment in journey order (stopovers included), for the trip request validation
    const flights = flightItemView.flights.flatMap((flight) =>
      flight.journeyFlight.flights.map((journeyFlight) => ({
        departingAirportCode: journeyFlight.departingAirport,
        arrivingAirportCode: journeyFlight.arrivalAirport,
        departingDate: journeyFlight.departingDate,
      })),
    )

    items.push({
      transactionKey: `${flightItemView.item.transactionKey}--${flightItemView.item.flights[0].journeyId}`,
      cost,
      travellers: resolveCartLineTravellers(context),
      itemPolicyLookup: buildItemPolicyLookup({
        itemPolicyType: 'FLIGHT',
        cacheKey: flightItemView.item.transactionKey,
        currencyCode: context.currencyCode,
        employeeIds: [],
        departingAirportCode,
        arrivingAirportCode,
        flightViewType: flightItemView.fareType === 'return' ? 'return' : 'oneWay',
        travellerCount: context.travellerCount,
      }),
      details: { flights },
    })
  }

  return items
}
